/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton
} from 'material-react-table';
import { LuiSelect, LuiModal, LuiPopover, LuiSelectItemEvent, LuiToolbar } from '@jda/lui-common-component-library-mui5';
import { ClickAwayListener, Box, Typography, Button, List, ListSubheader, ListItem } from '@mui/material';
import { ListUser, ShapeBulletCircleFill, UserAdd } from '@jda/lui-common-icon-library-mui5';
import { useStyles } from './styles';
import CustomSearch from '../../components/CustomSearch';
import UserTable from '../../components/UserTable';
import { getUsers, fetchIdps } from '../../store/user/users';
import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import { IUser } from '../../models/Users/UserContracts';
import { AlertDialog } from '../../shared/Dialog/AlertDialog';
import { getRoleOfSubject } from '../../services/roles';
import {
  associateDisassociateUserToLocation,
  getLocationsAssociatedToUser,
  getLocationsAssignedToUser,
  getRolesAssignedToUser,
  getRolesByGroupAssignedToUser,
  deleteDirectoryUser
} from '../../services/user';
import interpolateString from '../../utility/stringInterpolation';
import {
  selectAllFilteredUsers,
  selectSelectedItem,
  selectNoItem,
  selectSearch,
  selectSearchKey,
  selectLocationIdpId
} from '../../store/user/userSelector';
import { selectError } from '../../store/error/errorSelector';
import { selectShowHideUserLoader } from '../../store/loader/loaderSelector';
import { selectAppLocation } from '../../store/appLocation/appLocationSelector';
import {
  resetUsersState,
  setSearchUser,
  setSearchKey,
  setSelectedUserAction
} from '../../store/user/userActionCreator';
import {
  DELETE_USER_BUTTON_OK,
  BUTTON_CANCEL,
  DELETE_USER_TITLE,
  DELETE_USER_CONTENT,
  USER_ROLES_TITLE,
  USER_DELETE_ERROR_MESSAGE,
  USER_DELETE_SUCCESS_MESSAGE,
  SNACKBAR_ALERT_DEFAULT_PROPS,
  STATUS_CODES,
  DROPDOWN_MAX_HEIGHT,
  SEARCH_FILTER_KEYS,
  FOR,
  SEARCH_USER,
  ADD_USER,
  ASSIGNED_ROLES,
  NO_ROLES_MESSAGE_POPOVER,
  CLOSE,
  BULK_ROLE_ASSIGNMENT_LOWER_BOUND,
  BULK_ROLE_ASSIGNMENT_UPPER_BOUND,
  NO_LOCATIONS_MESSAGE_POPOVER,
  ASSIGNED_LOCATIONS
} from './constants';
import SnackbarAlert from '../../shared/SnackbarAlert';
import { IRole, IRoleByGroup, IRoles } from '../../models/Roles/RolesContracts';
import { permissions } from '../../shared/CheckPermissions/constants';
import { setSnackBarMessage, displaySnackbar } from '../../store/snackBar/snackBarActionCreator';
import { selectAllAssignedPermissions } from '../../store/auth/authSelector';
import { CheckViewPermission } from '../../shared/CheckPermissions/CheckViewPermission';
import { checkPermission } from '../../shared/CheckPermissions/CheckPermission';
import Loader from '../../shared/Loader';
import { showErrorAction } from '../../store/error/errorActionCreator';
import RoleAssignmentDialog from '../../components/RoleAssignmentDialog/RoleAssignmentDialog';
import { ASSIGN_ROLE } from '../../components/RoleAssignmentDialog/constants';
import { MANAGE_ROLES } from '../../components/UserTable/constants';
import { MESSAGES } from '../../components/UserDetails/constant';
import BulkRoleAssignments from '../../shared/BulkRoleAssignments';
import { ManageLocationsDialog } from '../../shared/ManageLocationsDialog';
import { getFeatureFlag as useFeatureFlag } from '@jda/lui-portal-utilities';
import { HARNESS_FEATURE_FLAG } from '../../utility/constants';
import { isPortalV2 } from '../../utility/commonUtility';

const Users = () => {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const history = useHistory();

  const noItem = useSelector(selectNoItem);
  const errors = useSelector(selectError);
  const selectSearchValue = useSelector(selectSearch);
  const searchKey = useSelector(selectSearchKey);
  const selectedItem = useSelector(selectSelectedItem);
  const filteredUsers = useSelector(selectAllFilteredUsers);
  const showLoader = useSelector(selectShowHideUserLoader);
  const appLocation = useSelector(selectAppLocation);
  const locationIdpId = useSelector(selectLocationIdpId);
  const assignedPermissions = useSelector(selectAllAssignedPermissions);
  const userGroupFeatureFlag = useFeatureFlag(HARNESS_FEATURE_FLAG.userGroup);
  const isUserGroupEnabled = isPortalV2() ? true : userGroupFeatureFlag;

  const userReadWritePermission = Boolean(
    assignedPermissions &&
    assignedPermissions.permissions &&
    checkPermission([permissions.user.read_write], assignedPermissions.permissions)
  );
  const roleAssignPermission =
    assignedPermissions &&
    assignedPermissions.permissions &&
    checkPermission([permissions.subject.read_assign], assignedPermissions.permissions);

  const [selectedUsers, setSelectedUsers] = useState<IUser[]>([]);
  const [showDialogLoader, setDialogHideLoader] = useState(false);
  const [selectedUserRoles, setSelectedUserRoles] = useState<IRole[]>([]);
  const [openDeleteConfirmationModal, setOpenDeleteConfirmationModal] = useState(false);
  const [roles, setRoles] = useState<IRoles | undefined>(undefined);
  const [locations, setLocations] = useState<any[]>([]);
  const [showPopoverLoader, showHidePopoverLoader] = useState(false);
  const [hasErrorOnRolesLocationsCall, setHasErrorOnRolesLocationsCall] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [popoverContentType, setPopoverContentType] = useState<string | null>(null);
  const [isBulkRoleAssignmentDialogOpen, setIsBulkRoleAssignmentDialogOpen] = useState(false);
  const [roleManagingUserId, setRoleManagingUserId] = useState<string>();
  const [locationAssigningState, setLocationAssigningState] = useState<{
    userId: string;
    locationsLoader: Promise<any>;
  }>();
  const [selectedUserIDP, setSelectedUserIDP] = useState<any>(null);


  const roleManagingUser = useMemo(() => filteredUsers.find((user: IUser) => user.id === roleManagingUserId), [
    filteredUsers,
    roleManagingUserId
  ]);

  const filterParams = useMemo(() =>
    selectSearchValue
      ? {
        'filter-by-key': searchKey?.key,
        'filter-by-value': selectSearchValue
      }
      : undefined,
    [selectSearchValue, searchKey?.key]
  );

  const getAllUsers = useCallback(
    () => {
      const search = {
        ...filterParams,
        ...(appLocation && { 'location-id': appLocation.id })
      };
      getUsers(dispatch, search, appLocation?.name);
    },
    [dispatch, filterParams, appLocation]
  );

  const handleChangePageOptions = (itemsPerPage: number, page: number) => {
    const search = {
      'page-number': page,
      'page-size': itemsPerPage,
      ...filterParams,
      ...(appLocation && { 'location-id': appLocation.id })
    };
    getUsers(dispatch, search, appLocation?.name);
  };

  useEffect(() => {
    getAllUsers();
  }, [getAllUsers]);

  useEffect(() => {
    dispatch(showErrorAction({ message: '' }));
    return () => {
      dispatch(resetUsersState());
    };
  }, [dispatch]);

  useEffect(() => {
    fetchIdps(dispatch).catch((_error: any) => {
      dispatch(displaySnackbar(MESSAGES.ERROR_IN_FETCHING_IDENTITY_PROVIDER_NAME, 'error'));
    });
  }, [dispatch]);

  const userDeleteConfirmationHandler = (rowData: any, idp: any) => {
    dispatch(setSelectedUserAction(rowData));
    setSelectedUserIDP(idp);
    if (rowData && rowData.id) {
      getRoleOfSubject(rowData.id)
        .then((response: IRole[]) => {
          setSelectedUserRoles(response);
          setOpenDeleteConfirmationModal(true);
        })
        .catch(_error => {
          setSelectedUserRoles([]);
          setOpenDeleteConfirmationModal(true);
        });
    }
  };

  const userDeleteHandler = async () => {
    const forceDelete = Boolean(selectedUserRoles && selectedUserRoles.length > 0);
    const selectedUserName = selectedItem!.userName;
    const selectedUserId = selectedItem!.id;

    setDialogHideLoader(true);

    const deleteUser = deleteDirectoryUser;

    try {
      await deleteUser(selectedUserId, forceDelete);

      setDialogHideLoader(false);
      getAllUsers();
      setOpenDeleteConfirmationModal(false);

      dispatch(
        setSnackBarMessage({
          ...SNACKBAR_ALERT_DEFAULT_PROPS,
          message: interpolateString([selectedUserName], USER_DELETE_SUCCESS_MESSAGE)
        })
      );
    } catch (error: any) {
      setDialogHideLoader(false);
      setOpenDeleteConfirmationModal(false);

      let errorMessage = `${USER_DELETE_ERROR_MESSAGE} ${error.message}`;
      if (
        error.response &&
        (error.response.status === STATUS_CODES.ERROR_404 || error.response.status === STATUS_CODES.ERROR_409)
      ) {
        if (error.response.data && error.response.data.message) {
          errorMessage = `${USER_DELETE_ERROR_MESSAGE} ${error.response.data.message}`;
        }
      }

      dispatch(
        setSnackBarMessage({
          ...SNACKBAR_ALERT_DEFAULT_PROPS,
          message: errorMessage,
          severity: 'error'
        })
      );
    }
  };

  const handleListItemClick = (listItem: IUser) => {
    dispatch(setSelectedUserAction(listItem));
  };

  const getRolesList = () => {
    let rolesListDt: any;
    if (selectedUserRoles && selectedUserRoles.length > 0) {
      const roleItems = selectedUserRoles.map((roleItem: IRole, index: number) => {
        return (
          <Box display="flex" key={index}>
            <ShapeBulletCircleFill fontSize="small" />
            <Typography>{roleItem.name}</Typography>
          </Box>
        );
      });
      rolesListDt = (
        <Box className={classes.rolesListContainer} data-testid="confirmation-box-user-role-list">
          <Typography>{USER_ROLES_TITLE}</Typography>
          <Box className={classes.rolesListContainer}>{roleItems}</Box>
        </Box>
      );
    } else {
      rolesListDt = '';
    }
    return rolesListDt;
  };
  const onSearchTextBoxChange = (searchValue: string) => {
    const searchStr = searchValue.trim();
    dispatch(setSearchUser(searchStr));
    if (searchStr.length >= 3) {
      dispatch(resetUsersState());
    }
  };

  const onClearInput = () => {
    dispatch(setSearchUser(''));
    dispatch(resetUsersState());
  };

  const handleDropdownChange = (event: LuiSelectItemEvent) => {
    const selectedValue: any = event.target.value;
    dispatch(setSearchKey(selectedValue));
    dispatch(setSearchUser(selectSearchValue || ''));
  };

  const handleManagePopoverContent = async (
    event: React.MouseEvent<HTMLButtonElement>,
    id: string,
    type: string
  ) => {
    setAnchorEl(event.currentTarget);
    setPopoverContentType(type);
    showHidePopoverLoader(true);

    try {
      if (type === 'roles') {
        const [individualRolesResult, groupRolesResult] = await Promise.allSettled([
          getRolesAssignedToUser(id),
          getRolesByGroupAssignedToUser(id)
        ]);

        const individualRoles: IRole[] = [];
        const groupRoles: IRoleByGroup[] = [];
        let withError: any = {};

        if (individualRolesResult.status === 'fulfilled') {
          individualRoles.push(...(individualRolesResult as PromiseFulfilledResult<IRole[]>).value);
        } else {
          withError = {
            individualRoles: undefined
          }
          dispatch(displaySnackbar('Some roles failed to load. Individual roles are not loaded', 'error'));
        }

        if (groupRolesResult.status === 'fulfilled') {
          groupRoles.push(...(groupRolesResult as PromiseFulfilledResult<any>).value.data);
        } else {
          withError = {
            ...withError,
            groupRoles: undefined
          }
          dispatch(displaySnackbar('Some roles failed to load. Roles By Group are not loaded', 'error'));
        }

        if (individualRolesResult.status === 'rejected' && groupRolesResult.status === 'rejected') {
          withError = {
            individualRoles: undefined,
            groupRoles: undefined
          }
          setHasErrorOnRolesLocationsCall(true);
          dispatch(displaySnackbar('Roles failed to load.', 'error'));
        }
        const roles: any = { individualRoles, groupRoles, withError }
        setRoles(roles);
      } else {
        const locations = await getLocationsAssignedToUser(id);
        setLocations(locations);
      }
    } catch (error: any) {
      setHasErrorOnRolesLocationsCall(true);
      dispatch(displaySnackbar(error?.message || 'Something went wrong', 'error'));
    } finally {
      showHidePopoverLoader(false);
    }
  };

  const handleRole = (roles: any) => {
    const individualRoles: IRole[] = roles?.individualRoles || [];
    const groupRoles: IRole[] = roles?.groupRoles || [];
    const withError: any = {};

    if ((individualRoles.length === 0 && groupRoles.length === 0) && Object.keys(withError).length === 0) {
      return (
        <Typography className={classes.popoverMessage} data-testid="popover-empty-message">
          {NO_ROLES_MESSAGE_POPOVER}
        </Typography>
      );
    }

    const individualRolesDivided: Record<'Roles' | 'IDP Roles', string[]> = {
      'Roles': [],
      'IDP Roles': []
    };

    const rolesByGroupDivided: Record<'Roles By Group', string[]> = {
      'Roles By Group': [],
    };

    individualRoles.forEach((role: IRole) => {
      const group = role.associatedFrom === 'LIAM' ? 'Roles' : 'IDP Roles';
      individualRolesDivided[group].push(role.name);
    });

    groupRoles
      .filter((role: any) => role.associatedFrom === 'Group')
      .forEach((role: any) => rolesByGroupDivided['Roles By Group'].push(role.name + ' (' + role.groupIdentifier + ')'));

    const renderRoleSection = (groupedRoles: Record<string, string[]>) => (
      <Box>
        {Object.entries(groupedRoles).map(([groupName, roleNames]) =>
          roleNames.length > 0 ? (
            <List key={groupName}>
              <ListSubheader classes={{ root: classes.listSubheader }}>
                {groupName}
              </ListSubheader>
              {roleNames.map((roleName) => (
                <ListItem key={roleName} classes={{ root: classes.listItem }}>
                  {roleName}
                </ListItem>
              ))}
            </List>
          ) : null
        )}
      </Box>
    );

    return (
      <Box>
        {renderRoleSection(individualRolesDivided)}
        {isUserGroupEnabled ? renderRoleSection(rolesByGroupDivided) : null}
      </Box>
    );
  };


  //const handleLocations = (locations: ILocation[]) => {
  const handleLocations = (locations: any) => {
    if (locations.length === 0) {
      return <Typography className={classes.popoverMessage}>{NO_LOCATIONS_MESSAGE_POPOVER}</Typography>;
    }

    return (
      <List>
        {locations.map((item: any) => (
          <ListItem key={item} classes={{ root: classes.listItem }}>
            {item.displayName || item.name}
          </ListItem>
        ))}s
      </List>
    );
  };

  const popoverContent = showPopoverLoader ? (
    <Box className={classes.loaderPopover}>
      <Loader showLoader={showPopoverLoader} />
    </Box>
  ) : (
    !hasErrorOnRolesLocationsCall && (popoverContentType === 'roles' ? handleRole(roles) : handleLocations(locations))
    //popoverContentType === 'roles' ? handleRole(roles) : handleLocations(locations)
  );
  const onClosePopOver = () => {
    Boolean(anchorEl) && setAnchorEl(null);
  };

  const handleManageRolesDialogClose = () => {
    setRoleManagingUserId(undefined);
  };

  const handleLocationDialogOpen = (userId: string) => {
    setLocationAssigningState({
      userId,
      locationsLoader: getLocationsAssociatedToUser(userId)
    });
  };

  const handleLocationDialogClose = () => {
    setLocationAssigningState(undefined);
  };
  const extendedToolbar = ({ table }) => {
    const leftComponents = {
      component: (
        <Box className={classes.topLeftHeaderContainer}>
          <Box className={classes.searchBarWrap}>
            <CustomSearch
              value={selectSearchValue}
              placeHolder={SEARCH_USER}
              onClearInput={onClearInput}
              onSearchChange={onSearchTextBoxChange}
            />
          </Box>
          <Typography variant='body1' padding='0 10px'>{FOR}</Typography>
          <LuiSelect
            size='small'
            inputLabel={''}
            id="user-search-key"
            selectedItem={searchKey}
            itemData={SEARCH_FILTER_KEYS}
            maxHeight={DROPDOWN_MAX_HEIGHT}
            onSelectionChange={handleDropdownChange}
            className={classes.selectDropdown}
          />
        </Box>
      )
    };
    const rightComponents = {
      component: (
        <Box className={classes.buttonPanel}>
          {(!appLocation || locationIdpId) && (
            <CheckViewPermission permission={[permissions.user.read_write]}>
              <Button
                size="small"
                color="primary"
                disableElevation
                variant="contained"
                className={classes.addUserButton}
                startIcon={<UserAdd />}
                data-testid="add-user-button"
                onClick={() => {
                  if (history.location.search) {
                    history.push(`/addUser${history.location.search}`);
                  } else {
                    history.push('/addUser');
                  }
                }}
              >
                {ADD_USER}
              </Button>
            </CheckViewPermission>
          )}
          {userReadWritePermission && roleAssignPermission && (
            <Button
              color="primary"
              variant="outlined"
              size='small'
              startIcon={<ListUser />}
              className={classes.assignRoleButton}
              disabled={
                selectedUsers.length < BULK_ROLE_ASSIGNMENT_LOWER_BOUND ||
                selectedUsers.length > BULK_ROLE_ASSIGNMENT_UPPER_BOUND
              }
              onClick={() => {
                setIsBulkRoleAssignmentDialogOpen(true);
              }}
            >
              {ASSIGN_ROLE}
            </Button>
          )}

          <ShowHideColumnsButton table={table} />
          <ToggleDensePaddingButton table={table} />
          <ToggleFullScreenButton table={table} />
        </Box>
      )
    };
    return <LuiToolbar className={classes.toolbarPanel} leftZone={leftComponents} rightZone={rightComponents} />;
  };
  return (
    <Box className={classes.root}>
      <UserTable
        loading={showLoader}
        filteredUsers={filteredUsers}
        handleListItemClick={handleListItemClick}
        handleDeleteUser={userDeleteConfirmationHandler}
        handleManageRoles={setRoleManagingUserId}
        handleManagePopoverContent={handleManagePopoverContent}
        handleAssignLocation={handleLocationDialogOpen}
        selectedItem={!noItem && selectedItem && Object.keys(selectedItem).length > 0 ? selectedItem : filteredUsers[0]}
        selectedRowsData={selectedUsers}
        onSelectedRowsDataChange={setSelectedUsers}
        onChangePageOptions={handleChangePageOptions}
        extendedToolbar={extendedToolbar}
      />
      {errors && <ErrorMessage message={errors.message} />}
      {isBulkRoleAssignmentDialogOpen && (
        <RoleAssignmentDialog
          selectedUsers={selectedUsers}
          onSuccess={() => {
            setSelectedUsers([]);
          }}
          onClose={() => {
            setIsBulkRoleAssignmentDialogOpen(false);
          }}
        />
      )}
      {openDeleteConfirmationModal && (
        <AlertDialog
          dialogTitle={DELETE_USER_CONTENT}
          dialogContentText={DELETE_USER_TITLE}
          recordName={selectedItem && selectedItem.userName}
          primaryButtonText={DELETE_USER_BUTTON_OK}
          primaryButtonColor='error'
          secondButtonText={BUTTON_CANCEL}
          handleOnCancel={() => setOpenDeleteConfirmationModal(false)}
          handleOnOk={userDeleteHandler}
          showDialogLoader={showDialogLoader}
          additionalContent={getRolesList()}
        />
      )}
      {roleManagingUser && (
        <LuiModal
          isOpen={true}
          modalTitle={`${MANAGE_ROLES}${appLocation ? ` for ${appLocation.displayName}` : ''}`}
          handleClose={handleManageRolesDialogClose}
          topDivider={true}
          bottomDivider={false}
          primaryAction={
            <Button
              onClick={handleManageRolesDialogClose}
              variant="contained"
              color="primary"
              disableElevation={true}
            >
              {CLOSE}
            </Button>
          }
          modalSize="lg"
        >
          <BulkRoleAssignments subjectId={roleManagingUser.id} name={roleManagingUser.userName} />
        </LuiModal>
      )}
      {locationAssigningState && (
        <ManageLocationsDialog
          currentLocationsLoader={locationAssigningState.locationsLoader}
          maxToAdd={100}
          maxToRemove={100}
          onSave={(toAdd, toRemove) =>
            associateDisassociateUserToLocation(
              locationAssigningState.userId,
              [
                toAdd.length > 0 ? { op: 'add', value: toAdd.map(location => location.id) } : [],
                toRemove.length > 0 ? { op: 'remove', value: toRemove.map(location => location.id) } : []
              ].flat()
            )
          }
          onClose={handleLocationDialogClose}
        />
      )}
      <SnackbarAlert />
      {Boolean(anchorEl) && (<ClickAwayListener onClickAway={onClosePopOver}>
        <Box>
          <LuiPopover
            title={popoverContentType === 'roles' ? ASSIGNED_ROLES : ASSIGNED_LOCATIONS}
            anchorEl={anchorEl}
            isOpen={Boolean(anchorEl)}
            onClosePopover={onClosePopOver}
            hasCloseIcon
            className={classes.popover}
            content={popoverContent}
            data-testid='popover'
          />
        </Box>
      </ClickAwayListener>)}
    </Box>
  );
};

const User = {
  component: Users,
  name: 'Users'
};
export default User;
